import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
    Facebook,
    Instagram,
    MapPin,
    Phone,
    Mail,
    Clock,
    ArrowUpRight,
    Utensils,
    Sandwich,
    IceCream2,
    GlassWater,
    Sparkles,
    ChefHat,
    X,
    ShoppingBag,
    Share2,
    Check,
    MessageSquare,
} from "lucide-react";

const ORDER_PROVIDERS = [
    {
        key: "ubereats",
        name: "Uber Eats",
        tag: "Delivery",
        url: "#",
        accent: "#06C167",
    },
    {
        key: "doordash",
        name: "DoorDash",
        tag: "Delivery",
        url: "#",
        accent: "#FF3008",
    },
    {
        key: "toast",
        name: "Toast",
        tag: "Pickup · Order Ahead",
        url: "#",
        accent: "#FF4C00",
    },
];

const IMG = {
    logo: "/stackhouse-logo-v3.webp",
    logoSmall: "/stackhouse-logo-v3-sm.png",
    hero: "https://images.unsplash.com/photo-1748721666723-1fe0f4e8a288?fm=jpg&q=85&w=2400&auto=format&fit=crop",
    burgers:
        "https://images.pexels.com/photos/12325105/pexels-photo-12325105.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    chopped:
        "https://images.pexels.com/photos/23531331/pexels-photo-23531331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    sandwich:
        "https://images.unsplash.com/photo-1627856820473-d8c217427918?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    waffles:
        "https://images.pexels.com/photos/31262972/pexels-photo-31262972.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    crepes:
        "https://images.unsplash.com/photo-1693857228114-f9fef8c2f604?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    froyo:
        "https://images.pexels.com/photos/32055276/pexels-photo-32055276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    drinks:
        "https://images.unsplash.com/photo-1771425732867-2a2f9ad34f34?fm=jpg&q=85&w=2000&auto=format&fit=crop",
    catering:
        "https://images.pexels.com/photos/24863057/pexels-photo-24863057.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800",
};

const FACEBOOK_URL = "https://www.facebook.com/share/1Cmb9VCYmR/?mibextid=wwXIfr";

const fadeUp = {
    hidden: { opacity: 0, y: 36 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, ease: [0.2, 0.8, 0.2, 1] },
    },
};

const stagger = {
    hidden: {},
    show: {
        transition: { staggerChildren: 0.12, delayChildren: 0.1 },
    },
};

const Reveal = ({ children, className = "", delay = 0 }) => (
    <motion.div
        className={className}
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        transition={{ delay }}
    >
        {children}
    </motion.div>
);

const OrderModal = ({ open, onClose }) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [open, onClose]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="order-modal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm px-4 sm:px-6"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
                        className="relative w-full max-w-2xl bg-[#0A0A0A] border sh-hairline"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b sh-hairline">
                            <div className="flex items-center gap-3 font-mono text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[var(--sh-red)]">
                                <span className="w-8 h-px bg-[var(--sh-red)]" />
                                Order Online
                            </div>
                            <button
                                type="button"
                                data-testid="order-modal-close"
                                onClick={onClose}
                                className="w-9 h-9 border sh-hairline flex items-center justify-center hover:border-[var(--sh-red)] hover:text-[var(--sh-red)] transition-colors"
                                aria-label="Close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="px-6 sm:px-8 py-7">
                            <h3 className="font-heading uppercase text-4xl sm:text-5xl leading-[0.9] tracking-tight">
                                Pick your <span className="text-[var(--sh-red)]">platform.</span>
                            </h3>
                            <p className="mt-3 text-white/65 text-sm sm:text-base leading-relaxed max-w-md">
                                Order online with our delivery partners — or stop by 558 Westcott St for pickup.
                            </p>

                            <ul className="mt-7 grid gap-3">
                                {ORDER_PROVIDERS.map((p) => (
                                    <li key={p.key}>
                                        <a
                                            href={p.url}
                                            data-testid={`order-provider-${p.key}`}
                                            target={p.url === "#" ? "_self" : "_blank"}
                                            rel="noreferrer"
                                            onClick={(e) => {
                                                if (p.url === "#") e.preventDefault();
                                            }}
                                            className="group flex items-center justify-between gap-4 px-5 py-5 border sh-hairline hover:border-[var(--sh-red)] transition-colors"
                                        >
                                            <span className="flex items-center gap-4 min-w-0">
                                                <span
                                                    className="w-10 h-10 flex items-center justify-center font-heading text-lg uppercase shrink-0"
                                                    style={{
                                                        background: p.accent,
                                                        color: "#0A0A0A",
                                                    }}
                                                >
                                                    {p.name.charAt(0)}
                                                </span>
                                                <span className="flex flex-col min-w-0">
                                                    <span className="font-heading text-2xl uppercase tracking-tight leading-none">
                                                        {p.name}
                                                    </span>
                                                    <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.28em] text-white/55">
                                                        {p.tag}
                                                    </span>
                                                </span>
                                            </span>
                                            <span className="flex items-center gap-3 shrink-0">
                                                <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.28em] text-white bg-[var(--sh-red)] px-2 py-1">
                                                    Order Now
                                                </span>
                                                <ArrowUpRight className="w-5 h-5 text-white/60 group-hover:text-[var(--sh-red)] transition-colors" />
                                            </span>
                                        </a>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-6 pt-5 border-t sh-hairline flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.28em] text-white/45">
                                <span>Stackhouse & Desserts · Syracuse, NY</span>
                                <span className="hidden sm:inline">Press ESC to close</span>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const SECTION_DOTS = [
    { id: "top", label: "Top" },
    { id: "menu", label: "Menu" },
    { id: "froyo", label: "Froyo" },
    { id: "drinks", label: "Drinks" },
    { id: "catering", label: "Catering" },
    { id: "location", label: "Visit" },
    { id: "contact", label: "Contact" },
];

const AnchorDots = () => {
    const [active, setActive] = useState("top");

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setActive(e.target.id);
                });
            },
            { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
        );
        SECTION_DOTS.forEach((s) => {
            const el = document.getElementById(s.id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    const handleClick = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const offset = id === "top" ? 0 : 90;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: "smooth" });
    };

    return (
        <nav
            data-testid="anchor-dots"
            aria-label="Section navigation"
            className="flex fixed right-3 md:right-6 lg:right-8 top-1/2 -translate-y-1/2 z-40 flex-col items-end gap-2.5 md:gap-3"
        >
            {SECTION_DOTS.map((s) => {
                const isActive = s.id === active;
                return (
                    <button
                        key={s.id}
                        type="button"
                        onClick={() => handleClick(s.id)}
                        data-testid={`dot-${s.id}`}
                        aria-label={`Jump to ${s.label}`}
                        aria-current={isActive ? "true" : undefined}
                        className="group relative flex items-center gap-3 outline-none"
                    >
                        <span
                            className={`hidden md:inline font-mono text-[10px] uppercase tracking-[0.28em] whitespace-nowrap transition-all duration-300 ${
                                isActive
                                    ? "opacity-100 text-[var(--sh-red)] translate-x-0"
                                    : "opacity-0 group-hover:opacity-100 group-hover:translate-x-0 translate-x-2 text-black/70"
                            }`}
                        >
                            {s.label}
                        </span>
                        <span
                            className={`relative block h-2 md:h-2.5 transition-all duration-300 ${
                                isActive
                                    ? "w-6 md:w-7 bg-[var(--sh-red)]"
                                    : "w-2 md:w-2.5 bg-black/25 group-hover:bg-[var(--sh-red)] group-hover:w-5"
                            }`}
                        />
                    </button>
                );
            })}
        </nav>
    );
};

// Reusable share trigger (Web Share API on mobile, clipboard fallback on desktop)
const useShare = () => {
    const [shared, setShared] = useState(false);
    const share = async () => {
        const data = {
            title: "Stackhouse & Desserts — Syracuse",
            text: "Smash burgers, hot chicken & desserts on Westcott. Open late.",
            url:
                typeof window !== "undefined"
                    ? window.location.origin
                    : "https://stackhousedeli.com",
        };
        try {
            if (navigator.share) {
                await navigator.share(data);
                return;
            }
            await navigator.clipboard.writeText(data.url);
            setShared(true);
            setTimeout(() => setShared(false), 2200);
        } catch (e) {
            // user cancelled or share unavailable — silent
        }
    };
    return { shared, share };
};

// Stackhouse SMS number — the "to" recipient for all sms: links on the site
const STACKHOUSE_SMS = "+16803145392";

// Pre-filled SMS link — opens iMessage / Messages with the Westcott address + site URL
const buildSmsHref = () => {
    const site =
        typeof window !== "undefined"
            ? window.location.origin
            : "https://stackhousedeli.com";
    const body = `Stackhouse & Desserts — 558 Westcott St, Syracuse, NY. Menu: ${site}`;
    // iOS expects sms:<number>?&body=... ; Android accepts the same — both honor the encoded body.
    return `sms:${STACKHOUSE_SMS}?&body=${encodeURIComponent(body)}`;
};

const TopNav = ({ onOrderClick }) => {
    const { shared, share } = useShare();
    return (
    <header
        data-testid="top-nav"
        className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-white/85 border-b sh-hairline"
    >
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 h-16 md:h-20 flex items-center justify-between">
            <a
                href="#top"
                data-testid="brand-logo"
                className="flex items-center gap-3 group"
            >
                <img
                    src={IMG.logoSmall}
                    alt="Stackhouse & Desserts"
                    className="h-12 md:h-14 w-12 md:w-14 object-contain group-hover:scale-105 transition-transform"
                />
                <span className="hidden md:inline font-mono text-[10px] tracking-[0.3em] text-[var(--sh-text-muted)] uppercase border sh-hairline px-2 py-1">
                    Westcott · Syracuse
                </span>
            </a>

            <nav className="hidden md:flex items-center gap-8 font-mono text-xs tracking-[0.22em] uppercase text-black/80">
                <a
                    href="#menu"
                    data-testid="nav-menu"
                    className="hover:text-[var(--sh-red)] transition-colors"
                >
                    Menu
                </a>
                <a
                    href="#froyo"
                    data-testid="nav-froyo"
                    className="hover:text-[var(--sh-red)] transition-colors"
                >
                    Froyo
                </a>
                <a
                    href="#drinks"
                    data-testid="nav-drinks"
                    className="hover:text-[var(--sh-red)] transition-colors"
                >
                    Drinks
                </a>
                <a
                    href="#catering"
                    data-testid="nav-catering"
                    className="hover:text-[var(--sh-red)] transition-colors"
                >
                    Catering
                </a>
                <a
                    href="#location"
                    data-testid="nav-location"
                    className="hover:text-[var(--sh-red)] transition-colors"
                >
                    Visit
                </a>
            </nav>

            <div className="flex items-center gap-2 md:gap-3">
                <a
                    href={buildSmsHref()}
                    data-testid="nav-text-menu-btn"
                    aria-label="Text the menu"
                    title="Text the menu to a friend"
                    className="font-mono text-[10px] md:text-xs tracking-[0.22em] uppercase px-3 md:px-4 py-2.5 inline-flex items-center gap-2 border border-black/15 text-[var(--sh-text)] hover:border-[var(--sh-red)] hover:text-[var(--sh-red)] transition-colors"
                >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Text Menu</span>
                </a>
                <button
                    type="button"
                    onClick={share}
                    data-testid="nav-share-btn"
                    aria-label={shared ? "Link copied" : "Share this page"}
                    title={shared ? "Link copied!" : "Share this page"}
                    className="font-mono text-[10px] md:text-xs tracking-[0.22em] uppercase px-3 md:px-4 py-2.5 inline-flex items-center gap-2 border border-black/15 text-[var(--sh-text)] hover:border-[var(--sh-red)] hover:text-[var(--sh-red)] transition-colors"
                >
                    {shared ? (
                        <Check className="w-3.5 h-3.5" />
                    ) : (
                        <Share2 className="w-3.5 h-3.5" />
                    )}
                    <span className="hidden sm:inline">
                        {shared ? "Copied" : "Share"}
                    </span>
                </button>
                <button
                    type="button"
                    onClick={onOrderClick}
                    data-testid="nav-cta-order"
                    className="font-mono text-[10px] md:text-xs tracking-[0.22em] uppercase px-4 py-2.5 sh-btn-primary inline-flex items-center gap-2"
                >
                    <ShoppingBag className="w-3.5 h-3.5" />
                    Order
                </button>
            </div>
        </div>
    </header>
    );
};

const Hero = ({ onOrderClick }) => (
    <section
        id="top"
        data-testid="hero-section"
        className="relative min-h-[100svh] w-full overflow-hidden bg-black"
    >
        {/* Soft radial orange glow behind the logo */}
        <div
            className="absolute inset-0 pointer-events-none"
            style={{
                background:
                    "radial-gradient(circle at 50% 38%, rgba(255,90,31,0.22) 0%, rgba(255,90,31,0.07) 35%, transparent 65%)",
            }}
        />

        {/* Top corner labels */}
        <div className="relative z-10 pt-24 md:pt-28 px-6 md:px-12 lg:px-20 max-w-[1480px] mx-auto">
            <div className="flex items-center justify-between font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-white/70">
                <span data-testid="hero-tagline-top" className="flex items-center gap-3">
                    <span className="inline-block w-8 h-px bg-[var(--sh-red)]" />
                    558 Westcott St · Syracuse, NY
                </span>
                <span className="hidden md:inline">Open Now · 7 Days</span>
            </div>
        </div>

        {/* Hero center: cartoon logo + supporting copy */}
        <div className="relative z-10 px-6 md:px-12 lg:px-20 max-w-[1480px] mx-auto pt-8 md:pt-10 pb-16 text-center">
            <motion.div
                initial="hidden"
                animate="show"
                variants={stagger}
                className="flex flex-col items-center"
            >
                <motion.div
                    variants={fadeUp}
                    data-testid="hero-open-pill"
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/20 font-mono text-[10px] tracking-[0.3em] uppercase text-white/85 mb-6"
                >
                    <span className="w-2 h-2 rounded-full bg-[var(--sh-red)] animate-pulse" />
                    Open Now · Westcott Nation
                </motion.div>

                <motion.img
                    variants={fadeUp}
                    data-testid="hero-logo"
                    src={IMG.logo}
                    alt="Stackhouse & Desserts — Smash Burgers & Hot Chicken Sandwiches"
                    className="w-auto h-[260px] sm:h-[340px] md:h-[400px] lg:h-[460px] max-h-[55vh] object-contain drop-shadow-[0_30px_60px_rgba(255,90,31,0.25)]"
                />

                <motion.p
                    variants={fadeUp}
                    data-testid="hero-subheading"
                    className="mt-8 font-mono uppercase tracking-[0.32em] text-white/80 text-xs sm:text-sm"
                >
                    Fresh. Crafted. Open Late.
                </motion.p>

                <motion.p
                    variants={fadeUp}
                    className="mt-5 max-w-2xl text-white/65 text-base sm:text-lg leading-relaxed"
                >
                    Smash burgers, Nashville hot chicken, tenders on Texas toast,
                    pressed paninis, hand-folded crepes, golden waffles, cold drinks
                    and creamy froyo — built bold, served fast on Westcott St.
                </motion.p>

                <motion.div
                    variants={fadeUp}
                    className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
                >
                    <a
                        href="#menu"
                        data-testid="hero-view-menu-btn"
                        className="sh-btn-primary font-mono uppercase tracking-[0.22em] text-xs px-7 py-4 inline-flex items-center justify-center gap-2"
                    >
                        View Menu <ArrowUpRight className="w-4 h-4" />
                    </a>
                    <button
                        type="button"
                        data-testid="hero-order-btn"
                        onClick={onOrderClick}
                        className="font-mono uppercase tracking-[0.22em] text-xs px-7 py-4 inline-flex items-center justify-center gap-2 border border-white/30 text-white bg-white/5 hover:border-[var(--sh-red)] hover:bg-[var(--sh-red)] hover:text-white transition-colors"
                    >
                        <ShoppingBag className="w-4 h-4" />
                        Order Online
                    </button>
                    <a
                        href={FACEBOOK_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="hero-facebook-btn"
                        className="sh-btn-ghost-on-dark font-mono uppercase tracking-[0.22em] text-xs px-7 py-4 inline-flex items-center justify-center gap-2"
                    >
                        <Facebook className="w-4 h-4" /> Follow on Facebook
                    </a>
                </motion.div>
            </motion.div>

            {/* Bottom credibility strip */}
            <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                transition={{ delay: 0.6 }}
                className="mt-16 md:mt-24 flex items-end justify-between border-t border-white/10 pt-6 font-mono text-[10px] sm:text-xs tracking-[0.28em] uppercase text-white/55"
            >
                <span data-testid="hero-cred-1">Hand-Cut · Daily</span>
                <span data-testid="hero-cred-2" className="hidden sm:inline">
                    Smash · Burgers
                </span>
                <span data-testid="hero-cred-3">Open 7 Days</span>
            </motion.div>
        </div>
    </section>
);

const ComingSoonMarquee = () => (
    <section
        data-testid="coming-soon-marquee"
        className="relative bg-[var(--sh-red)] text-white border-y border-black/30 overflow-hidden"
    >
        <Marquee speed={70} gradient={false} pauseOnHover>
            <div className="marquee-track py-5 md:py-7 font-heading uppercase text-3xl md:text-5xl lg:text-6xl tracking-tight">
                {Array.from({ length: 6 }).map((_, i) => (
                    <span key={i} className="flex items-center gap-12 pr-12">
                        Open Now
                        <span className="w-3 h-3 rounded-full bg-white/95" />
                        Stackhouse & Desserts
                        <span className="w-3 h-3 rounded-full bg-black/80" />
                        558 Westcott St · Syracuse, NY
                        <span className="w-3 h-3 rounded-full bg-white/95" />
                        Order Online
                        <span className="w-3 h-3 rounded-full bg-black/80" />
                    </span>
                ))}
            </div>
        </Marquee>
    </section>
);

const SectionHeader = ({ kicker, title, copy, idx }) => (
    <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-20">
        <div className="col-span-12 lg:col-span-5">
            <Reveal>
                <div className="flex items-center gap-3 font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[var(--sh-red)]">
                    <span className="font-mono text-black/40">
                        {String(idx).padStart(2, "0")}
                    </span>
                    <span className="w-8 h-px bg-[var(--sh-red)]" />
                    {kicker}
                </div>
            </Reveal>
            <Reveal delay={0.1}>
                <h2 className="mt-5 font-heading uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
                    {title}
                </h2>
            </Reveal>
        </div>
        <div className="col-span-12 lg:col-span-6 lg:col-start-7 self-end">
            <Reveal delay={0.18}>
                <p className="text-black/70 text-base sm:text-lg leading-relaxed max-w-xl">
                    {copy}
                </p>
            </Reveal>
        </div>
    </div>
);

import { MENU_CATEGORIES, COMBO_UPCHARGE } from "./menuData";

// Heat-level fire emojis (1–4)
const HeatFires = ({ level }) => {
    if (!level || level < 1) return null;
    return (
        <span
            data-testid={`heat-fires-${level}`}
            className="inline-flex items-center gap-0.5 text-base sm:text-lg"
        >
            {Array.from({ length: level }).map((_, i) => (
                <span key={i}>🔥</span>
            ))}
        </span>
    );
};

// Badge styling per type — bold colored chips
const BADGE_STYLES = {
    house: "bg-amber-200 text-black",
    spicy: "bg-orange-500 text-white",
    "extra-hot": "bg-red-600 text-white",
    "late-night": "border border-orange-400 text-orange-400 bg-transparent",
    mild: "bg-emerald-500 text-white",
    medium: "bg-yellow-400 text-black",
    hot: "bg-orange-500 text-white",
    "x-hot": "bg-red-600 text-white",
    signature: "bg-violet-500 text-white",
};

const ItemBadge = ({ text, type }) => (
    <span
        className={`inline-flex items-center font-mono text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 ${
            BADGE_STYLES[type] || BADGE_STYLES.house
        }`}
    >
        {text}
    </span>
);

// Accent stripe color per badge type
const accentForBadge = (type) => {
    switch (type) {
        case "house":
            return "#D4AF37";
        case "spicy":
        case "hot":
            return "#f97316";
        case "extra-hot":
        case "x-hot":
            return "#dc2626";
        case "late-night":
            return "#fb923c";
        case "mild":
            return "#10b981";
        case "medium":
            return "#eab308";
        case "signature":
            return "#8b5cf6";
        default:
            return null;
    }
};

// The new dark card used for every menu item
const DarkMenuCard = ({ item, cat, idx }) => {
    const accent = accentForBadge(item.badgeType);
    return (
        <div
            data-testid={`menu-item-${cat.id}-${idx}`}
            className="relative bg-[#0f0f10] text-white rounded-md overflow-hidden border border-black/30 transition-all hover:border-[var(--sh-red)]/40 hover:shadow-[0_10px_40px_rgba(0,0,0,0.18)] h-full"
        >
            {accent && (
                <span
                    className="absolute left-0 top-0 bottom-0 w-1.5"
                    style={{ background: accent }}
                />
            )}
            <div className={`p-5 sm:p-6 ${accent ? "pl-6 sm:pl-7" : ""}`}>
                <div className="flex flex-wrap items-center gap-3">
                    <h4 className="font-heading uppercase text-xl sm:text-2xl tracking-tight leading-tight text-white inline-flex items-center gap-2">
                        {item.emoji && (
                            <span className="text-2xl leading-none">
                                {item.emoji}
                            </span>
                        )}
                        {item.name}
                    </h4>
                    {item.badge && (
                        <ItemBadge text={item.badge} type={item.badgeType} />
                    )}
                </div>
                {item.desc && (
                    <p className="mt-3 text-white/70 text-sm sm:text-[15px] leading-relaxed">
                        {item.desc}
                    </p>
                )}
                {item.tags && item.tags.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                        {item.tags.map((t) => (
                            <span
                                key={t}
                                className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-white/55 border border-white/15 px-2.5 py-1"
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                )}
                {(cat.sizes || item.sizes || typeof item.price === "number" || item.heat) && (
                <div className="mt-5 flex flex-wrap items-baseline gap-x-4 gap-y-2">
                    {item.sizes ? (
                        <div className="flex flex-wrap items-baseline gap-4 font-mono">
                            {item.sizes.map((sz) => (
                                <div
                                    key={sz.label}
                                    className="flex items-baseline gap-1.5"
                                >
                                    <span className="text-[10px] uppercase tracking-[0.22em] text-white/50">
                                        {sz.label}
                                    </span>
                                    <span className="text-lg sm:text-xl text-[var(--sh-red)]">
                                        ${sz.price.toFixed(2)}
                                    </span>
                                </div>
                            ))}
                            <span className="text-[9px] uppercase tracking-[0.2em] text-white/35">
                                + tax
                            </span>
                            {item.combo && (
                                <span className="text-white/55 text-[10px] font-mono uppercase tracking-[0.18em]">
                                    Combo +${COMBO_UPCHARGE.toFixed(2)}
                                </span>
                            )}
                        </div>
                    ) : cat.sizes ? (
                        <div className="flex flex-wrap items-baseline gap-4 font-mono">
                            {cat.sizes.map((sz) => (
                                <div
                                    key={sz.label}
                                    className="flex items-baseline gap-1.5"
                                >
                                    <span className="text-[10px] uppercase tracking-[0.22em] text-white/50">
                                        {sz.label}
                                    </span>
                                    <span className="text-lg sm:text-xl text-[var(--sh-red)]">
                                        ${sz.price.toFixed(2)}
                                    </span>
                                </div>
                            ))}
                            <span className="text-[9px] uppercase tracking-[0.2em] text-white/35">
                                + tax
                            </span>
                            {item.combo && (
                                <span className="text-white/55 text-[10px] font-mono uppercase tracking-[0.18em]">
                                    Combo +${COMBO_UPCHARGE.toFixed(2)}
                                </span>
                            )}
                        </div>
                    ) : typeof item.price === "number" ? (
                        <>
                            <span className="font-heading text-3xl sm:text-4xl tracking-tight text-[var(--sh-red)]">
                                ${item.price.toFixed(2)}
                            </span>
                            {item.comboPrice && (
                                <span className="text-white/55 text-sm font-mono">
                                    Combo ${item.comboPrice.toFixed(2)}
                                </span>
                            )}
                            {!item.comboPrice && item.combo && (
                                <span className="text-white/55 text-[10px] font-mono uppercase tracking-[0.18em]">
                                    Combo +${COMBO_UPCHARGE.toFixed(2)}
                                </span>
                            )}
                            <span className="text-[9px] uppercase tracking-[0.2em] text-white/35">
                                + tax
                            </span>
                        </>
                    ) : null}
                    {item.heat ? (
                        <span className="ml-auto">
                            <HeatFires level={item.heat} />
                        </span>
                    ) : null}
                </div>
                )}
                {item.mealAddOns && item.mealAddOns.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                        <div className="font-mono uppercase tracking-[0.22em] text-[9px] text-[var(--sh-red)] mb-2">
                            Add-Ons
                        </div>
                        <div className="flex flex-col gap-1.5">
                            {item.mealAddOns.map((a) => (
                                <div
                                    key={a.name}
                                    className="flex items-baseline justify-between text-sm"
                                >
                                    <span className="text-white/80">{a.name}</span>
                                    <span className="text-[var(--sh-red)] font-mono">
                                        +${a.price.toFixed(2)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {item.reserveSms && (
                    <a
                        href={`sms:${STACKHOUSE_SMS}?&body=${encodeURIComponent(item.reserveSms)}`}
                        data-testid={`reserve-btn-${cat.id}-${idx}`}
                        className="mt-4 w-full font-mono uppercase tracking-[0.22em] text-[10px] px-4 py-3 sh-btn-primary inline-flex items-center justify-center gap-2"
                    >
                        <MessageSquare className="w-3.5 h-3.5" />
                        Reserve Your Pack
                    </a>
                )}
            </div>
        </div>
    );
};

// Orange category banner (shown for combo-eligible categories that define one)
const CategoryBanner = ({ text, testid }) => (
    <div
        data-testid={testid}
        className="bg-[var(--sh-red)] text-white text-center font-mono uppercase tracking-[0.25em] text-[11px] sm:text-xs px-6 py-4 sm:py-5 mb-8 md:mb-10"
    >
        {text}
    </div>
);

// 4-cell colored heat strip (chicken category only)
const HeatScaleStrip = ({ scale }) => (
    <div
        data-testid="heat-scale-strip"
        className="grid grid-cols-2 sm:grid-cols-4 border border-white/10 overflow-hidden bg-[#0f0f10] mb-10"
    >
        {scale.map((s) => (
            <div
                key={s.label}
                className="flex flex-col items-center justify-center p-4 sm:p-6 text-white border-r border-b sm:border-b-0 border-white/10 last:border-r-0"
                style={{
                    background: `linear-gradient(180deg, ${s.color}22, transparent 80%)`,
                }}
            >
                <span className="text-3xl sm:text-4xl">{s.emoji}</span>
                <span
                    className="mt-2 font-heading uppercase text-base sm:text-lg tracking-tight"
                    style={{ color: s.color }}
                >
                    {s.label}
                </span>
                <span className="mt-0.5 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-white/55 text-center">
                    {s.sub}
                </span>
            </div>
        ))}
    </div>
);

// "Pick Your Heat" colored detail cards
const PickYourHeat = ({ scale }) => (
    <div className="space-y-3" data-testid="pick-your-heat">
        {scale.map((s) => (
            <div
                key={s.label}
                className="relative bg-[#0f0f10] border border-white/10 overflow-hidden flex items-start gap-4 p-4 sm:p-5"
            >
                <span
                    className="absolute left-0 top-0 bottom-0 w-1.5"
                    style={{ background: s.color }}
                />
                <span className="text-3xl sm:text-4xl pl-2 leading-none mt-1">
                    {s.emoji}
                </span>
                <div className="flex-1 min-w-0">
                    <h5
                        className="font-heading uppercase text-lg sm:text-xl tracking-tight"
                        style={{ color: s.color }}
                    >
                        {s.label}
                    </h5>
                    <p className="mt-1 text-white/65 text-sm leading-relaxed">
                        {s.description || s.sub}
                    </p>
                </div>
            </div>
        ))}
    </div>
);

// "Make It A Combo" upsell card
const MakeItACombo = () => (
    <div
        data-testid="make-it-a-combo"
        className="relative bg-[#0f0f10] border border-white/10 overflow-hidden p-5 sm:p-6"
    >
        <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-orange-500" />
        <div className="pl-3">
            <h5 className="font-heading uppercase text-xl sm:text-2xl tracking-tight text-orange-500">
                Make It A Combo
            </h5>
            <p className="mt-2 text-white/65 text-sm leading-relaxed">
                Add crinkle-cut fries + a soda for just ${COMBO_UPCHARGE.toFixed(2)} more.
                Same heat, more food, better deal.
            </p>
            <div className="mt-3 flex items-baseline gap-3">
                <span className="font-heading text-3xl sm:text-4xl text-orange-500">
                    +${COMBO_UPCHARGE.toFixed(2)}
                </span>
                <span className="text-white/50 text-xs font-mono uppercase tracking-[0.2em]">
                    Upgrade
                </span>
            </div>
        </div>
    </div>
);

// Add-Ons block (used by burgers + chicken)
const AddOnsBlock = ({ addOns, testid }) => (
    <div
        data-testid={testid}
        className="bg-[#0f0f10] border border-white/10 p-5 sm:p-6"
    >
        <h5 className="font-heading uppercase text-xl tracking-tight text-white mb-4">
            Add-Ons
        </h5>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            {addOns.map((a) => (
                <div
                    key={a.name}
                    className="flex items-baseline justify-between border-b border-white/10 pb-2 text-sm"
                >
                    <span className="text-white/80">{a.name}</span>
                    <span className="text-[var(--sh-red)] font-mono">
                        +${a.price.toFixed(2)}
                    </span>
                </div>
            ))}
        </div>
    </div>
);

// "Good To Know" footnotes (used by burgers + chicken)
const GoodToKnow = ({ items, testid }) => (
    <div
        data-testid={testid}
        className="border border-white/10 bg-[#0f0f10] p-5 sm:p-6"
    >
        <h5 className="font-heading uppercase text-xl tracking-tight text-white mb-4">
            Good To Know
        </h5>
        <ul className="space-y-2 text-white/65 text-sm leading-relaxed">
            {items.map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                    <span className="text-[var(--sh-red)] mt-1">•</span>
                    <span>{t}</span>
                </li>
            ))}
        </ul>
    </div>
);

// Tenders peach-gradient hero (special header for the chicken category)
const TendersHero = () => (
    <div
        data-testid="tenders-hero"
        className="relative overflow-hidden mb-10 md:mb-12 border border-black/5"
        style={{
            background:
                "linear-gradient(180deg, #fdece1 0%, #fef5ed 60%, #ffffff 100%)",
        }}
    >
        <div className="relative z-10 px-6 md:px-12 py-12 md:py-16 text-center">
            <div className="font-mono uppercase tracking-[0.3em] text-[10px] sm:text-xs text-[var(--sh-red)]">
                Stackhouse Deli · 558 Westcott St, Syracuse NY
            </div>
            <h2
                className="mt-6 font-heading uppercase tracking-tight leading-[0.85] text-[var(--sh-red)] text-[20vw] sm:text-[15vw] lg:text-[11vw]"
                style={{ filter: "drop-shadow(0 6px 24px rgba(255,90,31,0.25))" }}
            >
                Tenders
            </h2>
            <div className="-mt-2 sm:-mt-4 font-heading uppercase tracking-[0.18em] text-[6vw] sm:text-[4.5vw] lg:text-[3vw] text-black/10 select-none pointer-events-none">
                On Texas Toast
            </div>
            <p className="mt-6 font-mono uppercase tracking-[0.3em] text-[10px] sm:text-xs text-black/70">
                Hand-Breaded. Spiced To Order. No Exceptions.
            </p>
        </div>
    </div>
);

// 3-column "Choose Your Heat" picker (Southern / Nashville / Inferno)
const ChooseYourHeat = ({ options }) => (
    <div
        data-testid="choose-your-heat"
        className="mb-10 md:mb-12 bg-[#0f0f10] border border-black/20 overflow-hidden"
    >
        <div className="px-5 sm:px-6 py-3 border-b border-white/10 font-mono text-[10px] sm:text-xs uppercase tracking-[0.28em] text-white/70 text-center">
            Choose Your Heat
        </div>
        <div className="grid grid-cols-3">
            {options.map((o) => (
                <div
                    key={o.label}
                    className="flex flex-col items-center justify-center gap-1 px-3 py-5 sm:py-6 border-r border-white/10 last:border-r-0 text-center"
                    style={{
                        background: `linear-gradient(180deg, ${o.color}22, transparent 80%)`,
                    }}
                >
                    <span className="text-2xl sm:text-3xl leading-none">
                        {o.emoji}
                    </span>
                    <span
                        className="mt-1 font-heading uppercase text-base sm:text-xl tracking-tight"
                        style={{ color: o.color }}
                    >
                        {o.label}
                    </span>
                    <span className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-white/55">
                        {o.sub}
                    </span>
                </div>
            ))}
        </div>
    </div>
);

// Build-Your-Own smash burger builder (multi-step layout)
const BuildYourOwn = ({ builder }) => (
    <div
        data-testid="build-your-own"
        className="mt-14 md:mt-16 border border-black/20 bg-[#0f0f10] overflow-hidden"
    >
        <div className="p-6 sm:p-8 border-b border-white/10">
            <div className="flex flex-wrap items-baseline gap-3">
                <h4 className="font-heading uppercase text-3xl sm:text-4xl tracking-tight leading-[0.9] text-white">
                    {builder.title}
                </h4>
                {builder.subtitle && (
                    <span className="font-mono uppercase text-[10px] tracking-[0.22em] text-white/50">
                        {builder.subtitle}
                    </span>
                )}
            </div>
            <div className="mt-4 flex flex-wrap items-baseline gap-3">
                <span className="font-heading text-3xl sm:text-4xl text-[var(--sh-red)]">
                    ${builder.basePrice.toFixed(2)}
                </span>
                {builder.comboUpcharge && (
                    <span className="text-white/60 text-sm font-mono">
                        Combo +${builder.comboUpcharge.toFixed(2)}
                    </span>
                )}
                <span className="text-[9px] uppercase tracking-[0.2em] text-white/35">
                    + tax
                </span>
                {builder.note && (
                    <span className="text-white/60 text-xs font-mono uppercase tracking-[0.18em]">
                        · {builder.note}
                    </span>
                )}
            </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
            {builder.steps.map((step, si) => (
                <div key={step.title} className="p-6 sm:p-7">
                    <div className="flex items-center gap-3 font-mono uppercase tracking-[0.22em] text-[10px] text-[var(--sh-red)] mb-4">
                        <span className="font-mono text-white/40">
                            {String(si + 1).padStart(2, "0")}
                        </span>
                        <span className="w-6 h-px bg-[var(--sh-red)]" />
                        {step.rule}
                    </div>
                    <h5 className="font-heading uppercase text-lg tracking-tight text-white mb-4">
                        {step.title}
                    </h5>
                    <ul className="grid grid-cols-1 gap-1.5">
                        {step.options.map((opt) => (
                            <li
                                key={opt}
                                className="flex items-center gap-2 text-white/75 text-sm"
                            >
                                <span className="w-1 h-1 bg-[var(--sh-red)] rounded-full" />
                                {opt}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
        {builder.addOns && (
            <div className="p-6 sm:p-8 border-t border-white/10">
                <div className="flex items-center gap-3 font-mono uppercase tracking-[0.22em] text-[10px] text-[var(--sh-red)] mb-4">
                    <span className="w-6 h-px bg-[var(--sh-red)]" />
                    Add-Ons
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
                    {builder.addOns.map((a) => (
                        <div
                            key={a.name}
                            className="flex items-baseline justify-between border-b border-white/10 pb-2 text-sm"
                        >
                            <span className="text-white/80">{a.name}</span>
                            <span className="text-[var(--sh-red)] font-mono">
                                +${a.price.toFixed(2)}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        )}
    </div>
);

const FullMenu = () => {
    const [active, setActive] = useState(MENU_CATEGORIES[0].id);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        const id = e.target.id.replace(/^cat-/, "");
                        setActive(id);
                    }
                });
            },
            { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
        );
        MENU_CATEGORIES.forEach((c) => {
            const el = document.getElementById(`cat-${c.id}`);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    const handleJump = (id) => {
        const el = document.getElementById(`cat-${id}`);
        if (!el) return;
        const offset = 140; // sticky nav + tab bar
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: "smooth" });
    };

    return (
        <section
            id="menu"
            data-testid="full-menu"
            className="relative bg-[var(--sh-bg)] pt-24 md:pt-32 pb-24 md:pb-32 px-6 md:px-12 lg:px-20"
        >
            <div className="max-w-[1480px] mx-auto">
                <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
                    <div className="col-span-12 lg:col-span-7">
                        <Reveal>
                            <div className="flex items-center gap-3 font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[var(--sh-red)]">
                                <span className="font-mono text-black/40">01</span>
                                <span className="w-8 h-px bg-[var(--sh-red)]" />
                                The Menu
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-5 font-heading uppercase text-5xl sm:text-6xl lg:text-8xl leading-[0.85] tracking-tight">
                                Built Bold.
                                <br />
                                <span className="text-[var(--sh-red)]">Served Fast.</span>
                            </h2>
                        </Reveal>
                    </div>
                </div>
            </div>

            {/* Sticky category nav */}
            <div
                data-testid="menu-tab-nav"
                className="sticky top-16 md:top-20 z-30 -mx-6 md:-mx-12 lg:-mx-20 mb-12 md:mb-16 backdrop-blur-xl bg-white/85 border-y sh-hairline"
            >
                <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
                    <div className="flex items-center gap-2 overflow-x-auto py-3 md:py-4 scrollbar-thin">
                        {MENU_CATEGORIES.map((c) => {
                            const isActive = c.id === active;
                            return (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => handleJump(c.id)}
                                    data-testid={`menu-tab-${c.id}`}
                                    className={`shrink-0 font-mono text-[10px] md:text-xs tracking-[0.22em] uppercase px-4 py-2.5 border transition-colors ${
                                        isActive
                                            ? "bg-[var(--sh-red)] text-white border-[var(--sh-red)]"
                                            : "border-black/15 text-black/75 hover:border-[var(--sh-red)] hover:text-[var(--sh-text)]"
                                    }`}
                                >
                                    {c.name}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="max-w-[1480px] mx-auto space-y-24 md:space-y-32">
                {MENU_CATEGORIES.map((cat, idx) => (
                    <div
                        key={cat.id}
                        id={`cat-${cat.id}`}
                        data-testid={`menu-cat-${cat.id}`}
                        className="scroll-mt-32"
                    >
                        {/* Category header */}
                        <Reveal>
                            <div className="grid grid-cols-12 gap-6 md:gap-10 mb-8 md:mb-10">
                                <div className="col-span-12 lg:col-span-7">
                                    <div className="flex items-center gap-3 font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[var(--sh-red)]">
                                        <span className="font-mono text-black/40">
                                            {String(idx + 1).padStart(2, "0")}
                                        </span>
                                        <span className="w-8 h-px bg-[var(--sh-red)]" />
                                        {cat.kicker}
                                    </div>
                                    <h3 className="mt-4 font-heading uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tight">
                                        {cat.name}
                                    </h3>
                                </div>
                                <div className="col-span-12 lg:col-span-5 self-end">
                                    <p className="text-black/65 text-base leading-relaxed max-w-md">
                                        {cat.copy}
                                    </p>
                                </div>
                            </div>
                        </Reveal>

                        {/* Orange banner */}
                        {cat.banner && (
                            <Reveal>
                                <CategoryBanner
                                    text={cat.banner}
                                    testid={`menu-cat-banner-${cat.id}`}
                                />
                            </Reveal>
                        )}

                        {/* Heat picker (Southern / Nashville / Inferno) */}
                        {cat.heatOptions && (
                            <Reveal>
                                <ChooseYourHeat options={cat.heatOptions} />
                            </Reveal>
                        )}

                        {/* Subsections OR flat items OR builder-only */}
                        {cat.subsections ? (
                            <div className="space-y-14 md:space-y-16">
                                {cat.subsections.map((sub, si) => (
                                    <div
                                        key={sub.title}
                                        data-testid={`subsection-${cat.id}-${si}`}
                                    >
                                        <Reveal>
                                            <div className="flex items-center gap-3 font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[var(--sh-red)] mb-5">
                                                <span className="w-8 h-px bg-[var(--sh-red)]" />
                                                {sub.title}
                                                {sub.note && (
                                                    <span className="normal-case tracking-normal text-black/50 text-xs font-sans ml-2">
                                                        · {sub.note}
                                                    </span>
                                                )}
                                            </div>
                                        </Reveal>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                                            {sub.items.map((item, i) => (
                                                <Reveal
                                                    key={item.name}
                                                    delay={Math.min(i * 0.04, 0.3)}
                                                >
                                                    <DarkMenuCard
                                                        item={item}
                                                        cat={cat}
                                                        idx={`${si}-${i}`}
                                                    />
                                                </Reveal>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : cat.items ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                                {cat.items.map((item, i) => (
                                    <Reveal
                                        key={item.name}
                                        delay={Math.min(i * 0.04, 0.3)}
                                    >
                                        <DarkMenuCard
                                            item={item}
                                            cat={cat}
                                            idx={i}
                                        />
                                    </Reveal>
                                ))}
                            </div>
                        ) : null}

                        {/* Build Your Own builder (burgers) */}
                        {cat.builder && (
                            <Reveal>
                                <BuildYourOwn builder={cat.builder} />
                            </Reveal>
                        )}

                        {/* Legacy addOns (kept for compat, none in new data) */}
                        {cat.addOns && (
                            <Reveal>
                                <div className="mt-10">
                                    <AddOnsBlock
                                        addOns={cat.addOns}
                                        testid={`addons-${cat.id}`}
                                    />
                                </div>
                            </Reveal>
                        )}
                    </div>
                ))}
            </div>

            <div className="max-w-[1480px] mx-auto mt-24 md:mt-32 border-t sh-hairline pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="font-mono text-xs tracking-[0.28em] uppercase text-black/55">
                    Menu subject to change · Halal options available
                </p>
                <a
                    href={FACEBOOK_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="menu-follow-fb-btn"
                    className="sh-btn-ghost font-mono uppercase tracking-[0.22em] text-xs px-6 py-3 inline-flex items-center gap-2"
                >
                    <Facebook className="w-4 h-4" />
                    Follow for Updates
                </a>
            </div>
        </section>
    );
};

const FroyoSection = () => (
    <section
        id="froyo"
        data-testid="froyo-section"
        className="relative py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[var(--sh-bg)] border-t sh-hairline"
    >
        <div className="max-w-[1480px] mx-auto grid grid-cols-12 gap-6 md:gap-10 items-center">
            <Reveal className="col-span-12 lg:col-span-6 order-2 lg:order-1">
                <div className="flex items-center gap-3 font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[var(--sh-red)]">
                    <span className="font-mono text-black/40">02</span>
                    <span className="w-8 h-px bg-[var(--sh-red)]" />
                    Froyo & Desserts
                </div>
                <h2 className="mt-5 font-heading uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
                    Cold.
                    <br />
                    <span className="text-[var(--sh-steel)]">Creamy.</span>
                    <br />
                    <span className="text-[var(--sh-red)]">Loaded.</span>
                </h2>
                <p className="mt-6 text-black/70 text-base sm:text-lg leading-relaxed max-w-xl">
                    Self-serve premium frozen yogurt with a wall of toppings —
                    fresh fruit, candy, syrups, sprinkles. Build it your way.
                    Eat it slow. Or don’t.
                </p>

                <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
                    {[
                        { i: <IceCream2 className="w-4 h-4" />, t: "12+ Flavors" },
                        { i: <Sparkles className="w-4 h-4" />, t: "30+ Toppings" },
                        { i: <Utensils className="w-4 h-4" />, t: "Dairy-Free" },
                    ].map((c, idx) => (
                        <div
                            key={idx}
                            data-testid={`froyo-chip-${idx}`}
                            className="border sh-hairline px-3 py-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-black/80"
                        >
                            {c.i}
                            {c.t}
                        </div>
                    ))}
                </div>

                <a
                    href="#menu"
                    data-testid="froyo-cta"
                    className="mt-8 sh-btn-primary font-mono uppercase tracking-[0.22em] text-xs px-7 py-4 inline-flex items-center justify-center gap-2 w-fit"
                >
                    Build Your Cup <ArrowUpRight className="w-4 h-4" />
                </a>
            </Reveal>

            <Reveal
                delay={0.15}
                className="col-span-12 lg:col-span-6 order-1 lg:order-2"
            >
                <div className="sh-image-wrap relative overflow-hidden border sh-hairline aspect-[4/5] lg:aspect-[5/6]">
                    <img
                        src={IMG.froyo}
                        alt="Premium frozen yogurt"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.28em] uppercase text-white/85 bg-black/55 backdrop-blur-md px-3 py-2 border sh-hairline-on-dark">
                        Froyo · Self Serve
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);

const DrinksSection = () => (
    <section
        id="drinks"
        data-testid="drinks-section"
        className="relative py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[var(--sh-surface)] border-t sh-hairline overflow-hidden"
    >
        <div className="max-w-[1480px] mx-auto grid grid-cols-12 gap-6 md:gap-10 items-stretch">
            <Reveal className="col-span-12 lg:col-span-7">
                <div className="sh-image-wrap relative overflow-hidden border sh-hairline aspect-[16/10]">
                    <img
                        src={IMG.drinks}
                        alt="Refreshing drinks"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/10 to-transparent" />
                    <h2 className="absolute bottom-6 left-6 right-6 font-heading uppercase text-6xl sm:text-7xl lg:text-[8vw] leading-[0.85] tracking-tight text-white">
                        Cold
                        <br />
                        <span className="text-[var(--sh-red)]">Drinks</span>
                    </h2>
                </div>
            </Reveal>

            <Reveal delay={0.15} className="col-span-12 lg:col-span-5 flex flex-col">
                <div className="flex items-center gap-3 font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[var(--sh-red)]">
                    <span className="font-mono text-black/40">03</span>
                    <span className="w-8 h-px bg-[var(--sh-red)]" />
                    Beverages
                </div>
                <h3 className="mt-5 font-heading uppercase text-4xl sm:text-5xl leading-[0.95] tracking-tight">
                    Fresh-pressed,
                    <br />
                    iced & made-to-order.
                </h3>
                <p className="mt-5 text-black/70 leading-relaxed">
                    Smoothies, milkshakes, fresh juice, iced lattes, sodas
                    on tap. Big cups, real ice, no shortcuts.
                </p>

                <ul className="mt-8 divide-y sh-hairline border-y sh-hairline font-mono text-xs uppercase tracking-[0.22em]">
                    {[
                        ["Smoothies", "Mango · Berry · Tropical"],
                        ["Milkshakes", "Hand-spun, thick"],
                        ["Iced Coffee", "Cold brew · Lattes"],
                        ["Fresh Juice", "Squeezed daily"],
                        ["Sodas", "Fountain · Bottled"],
                    ].map(([k, v], i) => (
                        <li
                            key={i}
                            data-testid={`drink-row-${i}`}
                            className="flex items-center justify-between py-4 text-black/85"
                        >
                            <span>{k}</span>
                            <span className="text-black/45 normal-case tracking-[0.04em] font-body text-sm">
                                {v}
                            </span>
                        </li>
                    ))}
                </ul>
            </Reveal>
        </div>
    </section>
);

const Catering = () => (
    <section
        id="catering"
        data-testid="catering-section"
        className="relative min-h-[80vh] flex items-center px-6 md:px-12 lg:px-20 py-24 md:py-32 overflow-hidden"
    >
        <div className="absolute inset-0">
            <img
                src={IMG.catering}
                alt="Catering spread"
                className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/65" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/30" />
        </div>

        <div className="relative z-10 max-w-[1480px] mx-auto w-full grid grid-cols-12 gap-6 md:gap-10">
            <Reveal className="col-span-12 lg:col-span-7">
                <div className="backdrop-blur-2xl bg-black/55 border border-white/10 p-8 md:p-12">
                    <div className="flex items-center gap-3 font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[var(--sh-red)]">
                        <span className="font-mono text-white/40">04</span>
                        <span className="w-8 h-px bg-[var(--sh-red)]" />
                        Catering
                    </div>
                    <h2 className="mt-5 font-heading uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
                        Feed the
                        <br />
                        <span className="text-[var(--sh-red)]">whole crew.</span>
                    </h2>
                    <p className="mt-6 text-white/80 leading-relaxed max-w-xl">
                        Office lunches, game days, weddings, watch parties —
                        we bring the deli to you. Build-your-own sandwich
                        platters, hot trays of chopped cheese, sweet boxes,
                        froyo bars on wheels.
                    </p>

                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[
                            { i: <ChefHat className="w-4 h-4" />, t: "Custom Menus" },
                            { i: <Sandwich className="w-4 h-4" />, t: "Platters & Trays" },
                            { i: <GlassWater className="w-4 h-4" />, t: "Drink Stations" },
                        ].map((c, i) => (
                            <div
                                key={i}
                                data-testid={`catering-chip-${i}`}
                                className="border sh-hairline px-4 py-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/85 bg-black/30"
                            >
                                {c.i}
                                {c.t}
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <a
                            href="#contact"
                            data-testid="catering-contact-btn"
                            className="sh-btn-primary font-mono uppercase tracking-[0.22em] text-xs px-7 py-4 inline-flex items-center justify-center gap-2"
                        >
                            Inquire Now <ArrowUpRight className="w-4 h-4" />
                        </a>
                        <a
                            href="mailto:contact@stackhousedeli.com"
                            data-testid="catering-email-btn"
                            className="sh-btn-ghost-on-dark font-mono uppercase tracking-[0.22em] text-xs px-7 py-4 inline-flex items-center justify-center gap-2"
                        >
                            <Mail className="w-4 h-4" />
                            contact@stackhousedeli.com
                        </a>
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);

const LocationHours = () => {
    const hours = [
        ["Mon", "10:00 — 22:00"],
        ["Tue", "10:00 — 22:00"],
        ["Wed", "10:00 — 22:00"],
        ["Thu", "10:00 — 23:00"],
        ["Fri", "10:00 — 00:00"],
        ["Sat", "09:00 — 00:00"],
        ["Sun", "09:00 — 22:00"],
    ];
    return (
        <section
            id="location"
            data-testid="location-hours"
            className="relative py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[var(--sh-bg)] border-t sh-hairline"
        >
            <div className="max-w-[1480px] mx-auto">
                <SectionHeader
                    idx={5}
                    kicker="Visit / Hours"
                    title={
                        <>
                            Find Us
                            <br />
                            in Westcott.
                        </>
                    }
                    copy="Right by the action. Easy walk, quick stop, open late."
                />

                <div className="grid grid-cols-12 gap-6 md:gap-10">
                    {/* Map / address */}
                    <Reveal className="col-span-12 lg:col-span-7">
                        <div className="border sh-hairline overflow-hidden">
                            <div className="aspect-[16/10] bg-[var(--sh-muted)] relative">
                                <iframe
                                    title="Stackhouse & Desserts Location"
                                    src="https://www.google.com/maps?q=558+Westcott+St+Syracuse+NY+13210&output=embed"
                                    className="absolute inset-0 w-full h-full"
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x sh-hairline">
                                <div className="p-6">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-black/50 flex items-center gap-2">
                                        <MapPin className="w-3.5 h-3.5 text-[var(--sh-red)]" />
                                        Address
                                    </div>
                                    <div className="mt-2 text-black/90">
                                        558 Westcott St
                                        <br />
                                        Syracuse, NY 13210
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-black/50 flex items-center gap-2">
                                        <Phone className="w-3.5 h-3.5 text-[var(--sh-red)]" />
                                        Phone
                                    </div>
                                    <a
                                        href="tel:+13152144074"
                                        data-testid="location-phone"
                                        className="mt-2 block text-black/90 hover:text-[var(--sh-red)] transition-colors"
                                    >
                                        (315) 214-4074
                                    </a>
                                </div>
                                <div className="p-6">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-black/50 flex items-center gap-2">
                                        <Mail className="w-3.5 h-3.5 text-[var(--sh-red)]" />
                                        Email
                                    </div>
                                    <a
                                        href="mailto:contact@stackhousedeli.com"
                                        data-testid="location-email"
                                        className="mt-2 block text-black/90 hover:text-[var(--sh-red)] transition-colors break-all"
                                    >
                                        contact@stackhousedeli.com
                                    </a>
                                </div>
                            </div>
                        </div>
                    </Reveal>

                    {/* Hours */}
                    <Reveal delay={0.15} className="col-span-12 lg:col-span-5">
                        <div className="border sh-hairline">
                            <div className="px-6 py-5 flex items-center justify-between border-b sh-hairline">
                                <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-black/60">
                                    <Clock className="w-3.5 h-3.5 text-[var(--sh-red)]" />
                                    Hours of Operation
                                </div>
                                <span
                                    data-testid="hours-status-pill"
                                    className="font-mono text-[10px] uppercase tracking-[0.28em] text-white bg-[var(--sh-red)] px-2 py-1"
                                >
                                    Open Now
                                </span>
                            </div>
                            <ul className="font-mono text-sm">
                                {hours.map(([d, h], i) => (
                                    <li
                                        key={d}
                                        data-testid={`hours-row-${i}`}
                                        className="flex items-center justify-between px-6 py-4 border-b last:border-b-0 sh-hairline"
                                    >
                                        <span className="uppercase tracking-[0.2em] text-black/85">
                                            {d}
                                        </span>
                                        <span className="text-black/60">{h}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="px-6 py-5 border-t sh-hairline flex items-center justify-between">
                                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-black/50">
                                    Holidays
                                </span>
                                <span className="font-mono text-xs text-black/80">
                                    Reduced hours
                                </span>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

const Contact = () => (
    <section
        id="contact"
        data-testid="contact-section"
        className="relative py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[var(--sh-surface)] border-t sh-hairline"
    >
        <div className="max-w-[1480px] mx-auto grid grid-cols-12 gap-6 md:gap-10 items-end">
            <Reveal className="col-span-12 lg:col-span-7">
                <div className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[var(--sh-red)] flex items-center gap-3">
                    <span className="font-mono text-black/40">06</span>
                    <span className="w-8 h-px bg-[var(--sh-red)]" />
                    Get In Touch
                </div>
                <h2 className="mt-5 font-heading uppercase text-5xl sm:text-6xl lg:text-8xl leading-[0.85] tracking-tight">
                    Hungry?
                    <br />
                    <span className="text-[var(--sh-red)]">Holler at us.</span>
                </h2>
            </Reveal>

            <Reveal delay={0.15} className="col-span-12 lg:col-span-5">
                <div className="space-y-4">
                    <a
                        href={FACEBOOK_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="contact-facebook-btn"
                        className="sh-btn-primary font-mono uppercase tracking-[0.22em] text-xs px-7 py-5 inline-flex items-center justify-between gap-4 w-full"
                    >
                        <span className="flex items-center gap-3">
                            <Facebook className="w-5 h-5" />
                            Follow Us on Facebook
                        </span>
                        <ArrowUpRight className="w-5 h-5" />
                    </a>
                    <a
                        href="https://www.instagram.com/"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="contact-instagram-btn"
                        className="sh-btn-ghost font-mono uppercase tracking-[0.22em] text-xs px-7 py-5 inline-flex items-center justify-between gap-4 w-full"
                    >
                        <span className="flex items-center gap-3">
                            <Instagram className="w-5 h-5" />
                            Follow on Instagram
                        </span>
                        <ArrowUpRight className="w-5 h-5" />
                    </a>
                    <a
                        href="mailto:contact@stackhousedeli.com"
                        data-testid="contact-email-btn"
                        className="sh-btn-ghost font-mono uppercase tracking-[0.22em] text-xs px-7 py-5 inline-flex items-center justify-between gap-4 w-full"
                    >
                        <span className="flex items-center gap-3">
                            <Mail className="w-5 h-5" />
                            contact@stackhousedeli.com
                        </span>
                        <ArrowUpRight className="w-5 h-5" />
                    </a>
                </div>
            </Reveal>
        </div>
    </section>
);

const Footer = () => {
    const { shared, share: handleShare } = useShare();

    return (
    <footer
        data-testid="site-footer"
        className="relative bg-[var(--sh-bg)] border-t sh-hairline"
    >
        {/* Stainless steel band */}
        <div
            data-testid="footer-steel-band"
            className="sh-steel-band h-1.5 w-full"
        />

        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 pt-20 md:pt-28 pb-10">
            <img
                src={IMG.logo}
                alt="Stackhouse & Desserts"
                data-testid="footer-logo"
                className="block mx-auto h-[180px] sm:h-[220px] md:h-[280px] lg:h-[340px] w-auto object-contain"
            />

            <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-8">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-black/50">
                        Hours
                    </div>
                    <div className="mt-3 text-black/85 font-mono text-sm">
                        Mon–Sun
                        <br />
                        10:00 — 22:00
                    </div>
                </div>
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-black/50">
                        Address
                    </div>
                    <div className="mt-3 text-black/85 font-mono text-sm">
                        558 Westcott St
                        <br />
                        Syracuse, NY 13210
                    </div>
                </div>
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-black/50">
                        Phone
                    </div>
                    <a
                        href="tel:+13152144074"
                        className="mt-3 block text-black/85 font-mono text-sm hover:text-[var(--sh-red)]"
                    >
                        (315) 214-4074
                    </a>
                </div>
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-black/50">
                        Social
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                        <a
                            href={FACEBOOK_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-fb"
                            aria-label="Follow on Facebook"
                            className="w-10 h-10 bg-[var(--sh-red)] text-white flex items-center justify-center hover:bg-black transition-colors"
                        >
                            <Facebook className="w-4 h-4" />
                        </a>
                        <a
                            href="https://www.instagram.com/"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-ig"
                            aria-label="Follow on Instagram"
                            className="w-10 h-10 bg-[var(--sh-red)] text-white flex items-center justify-center hover:bg-black transition-colors"
                        >
                            <Instagram className="w-4 h-4" />
                        </a>
                        <button
                            type="button"
                            onClick={handleShare}
                            data-testid="footer-share-btn"
                            aria-label="Share this page"
                            title={shared ? "Link copied!" : "Share this page"}
                            className="w-10 h-10 bg-[var(--sh-red)] text-white flex items-center justify-center hover:bg-black transition-colors relative"
                        >
                            {shared ? (
                                <Check className="w-4 h-4" />
                            ) : (
                                <Share2 className="w-4 h-4" />
                            )}
                        </button>
                    </div>
                    {shared && (
                        <p
                            data-testid="footer-share-toast"
                            className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--sh-red)]"
                        >
                            Link copied!
                        </p>
                    )}
                </div>
            </div>

            <div className="mt-16 pt-6 border-t sh-hairline flex flex-col md:flex-row items-start md:items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-black/50">
                <span>© {new Date().getFullYear()} Stackhouse & Desserts · Syracuse, NY</span>
                <span>Fresh · Crafted · Open Late</span>
            </div>
        </div>
    </footer>
    );
};

function App() {
    const [orderOpen, setOrderOpen] = useState(false);
    return (
        <div className="App sh-grain bg-[var(--sh-bg)] text-[var(--sh-text)] selection:bg-[var(--sh-red)]">
            <TopNav onOrderClick={() => setOrderOpen(true)} />
            <main>
                <Hero onOrderClick={() => setOrderOpen(true)} />
                <ComingSoonMarquee />
                <FullMenu />
                <FroyoSection />
                <DrinksSection />
                <Catering />
                <LocationHours />
                <Contact />
            </main>
            <Footer />
            <OrderModal open={orderOpen} onClose={() => setOrderOpen(false)} />
        </div>
    );
}

export default App;
